import type { Card as CardDto, CardType } from "../../types/domain";
import { IcoClock, IcoTrash } from "../../components/icons";
import { isTouchLike } from "../../lib/device";
import { useCardViewers } from "../../hooks/useCardPresence";
import { useCardPermissions } from "../../hooks/useCardPermissions";

interface BoardCardProps {
  card: CardDto;
  type: CardType;
  onOpen: () => void;
  onDelete: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  isDragging: boolean;
  draggable?: boolean;
}

const STATUS_LABEL: Record<CardDto["status"], string> = {
  draft: "Черновик",
  ready: "Готово",
  sent: "Отправлено",
};

export function BoardCard({
  card,
  type,
  onOpen,
  onDelete,
  onDragStart,
  onDragEnd,
  isDragging,
  draggable = true,
}: BoardCardProps) {
  const style = {
    "--card-accent": type.color,
    "--type-color": type.color,
    "--type-bg": type.bg,
    "--type-text": type.text,
    cursor: "pointer",
  } as React.CSSProperties;

  const previewText = card.preview ? card.preview.replace(/\s+/g, " ").trim() : "";
  const cardPerms = useCardPermissions(card);
  const viewers = useCardViewers(card.launchId, card.id);
  const presenceTitle =
    viewers.length > 0
      ? `Сейчас смотрят: ${viewers.map((v) => v.name || v.email).join(", ")}`
      : undefined;
  // HTML5 DnD не работает на touch-устройствах (iOS/Android) — включаем только
  // когда у пользователя есть нормальный mouse/trackpad. Иначе draggable=true
  // ломает нативный scroll: long-press вместо прокрутки пытается тащить карточку.
  const canDrag = draggable && !isTouchLike();

  return (
    <div
      className={
        "card" +
        (isDragging ? " dragging" : "") +
        (cardPerms.canDelete ? " card--deletable" : "")
      }
      style={style}
      onClick={onOpen}
      draggable={canDrag}
      onDragStart={
        canDrag
          ? (e) => {
              e.dataTransfer.effectAllowed = "move";
              try {
                e.dataTransfer.setData("application/x-launchlab-card", card.id);
              } catch {
                // ignore — some browsers are picky about setData during non-user events
              }
              onDragStart();
            }
          : undefined
      }
      onDragEnd={canDrag ? onDragEnd : undefined}
      data-card-id={card.id}
    >
      {cardPerms.canDelete && (
        <button
          type="button"
          className="card-menu card-quick-delete"
          aria-label={`Удалить карточку «${card.title}»`}
          title="Удалить карточку"
          draggable={false}
          onMouseDown={(event) => event.stopPropagation()}
          onDragStart={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          <IcoTrash size={13} />
        </button>
      )}
      <div className="card-head">
        <span className="type-badge">
          <span className="type-dot" />
          {type.name}
        </span>
        {card.time && (
          <span className="card-time">
            <IcoClock size={11} />
            {card.time}
          </span>
        )}
      </div>
      {viewers.length > 0 && (
        <span className="card-presence" title={presenceTitle} aria-label={presenceTitle}>
          <span className="card-presence-dot" />
          {viewers.length > 1 && <span className="card-presence-count">{viewers.length}</span>}
        </span>
      )}
      <h3 className="card-title">{card.title}</h3>
      {previewText && <p className="card-preview">{previewText}</p>}
      <div className="card-foot">
        <span className={"card-status status-" + card.status}>
          <span className="status-dot" />
          {STATUS_LABEL[card.status]}
        </span>
      </div>
    </div>
  );
}
